import React, { useEffect, useRef, useState, useCallback } from 'react';
import '../styles/PongGame.css';
import API from '../services/api';

const PongGame = ({ visitorId }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('start');
  const [playerScore, setPlayerScore] = useState(0);
  const [aiScore, setAiScore] = useState(0);
  const [difficulty, setDifficulty] = useState('medium');
  const [gameSessionId, setGameSessionId] = useState(null);

  const gameRef = useRef(null);
  const keysRef = useRef({});
  const animationFrameRef = useRef(null);
  const scoreRef = useRef({ player: 0, ai: 0 });

  // Game constants
  const CANVAS_WIDTH = 800;
  const CANVAS_HEIGHT = 600;
  const PADDLE_WIDTH = 12;
  const PADDLE_HEIGHT = 100;
  const BALL_SIZE = 12;
  const WINNING_SCORE = 11;
  const PADDLE_SPEED = 11;

  // Difficulty-based speed settings
  const getSpeedSettings = (diff) => {
    switch(diff) {
      case 'easy':
        return { initial: 5, max: 14, multiplier: 1.04 };
      case 'hard':
        return { initial: 7.5, max: 20, multiplier: 1.06 };
      default: // medium
        return { initial: 6.5, max: 17, multiplier: 1.05 };
    }
  };

  // Initialize game
  const initGame = useCallback(() => {
    const speedSettings = getSpeedSettings(difficulty);

    gameRef.current = {
      ball: {
        x: CANVAS_WIDTH / 2,
        y: CANVAS_HEIGHT / 2,
        vx: 0,
        vy: 0,
        speed: speedSettings.initial
      },
      player: {
        x: 30,
        y: CANVAS_HEIGHT / 2 - PADDLE_HEIGHT / 2,
        speed: PADDLE_SPEED
      },
      ai: {
        x: CANVAS_WIDTH - 30 - PADDLE_WIDTH,
        y: CANVAS_HEIGHT / 2 - PADDLE_HEIGHT / 2,
        speed: PADDLE_SPEED * 0.7
      },
      lastResetTime: Date.now(),
      speedSettings: speedSettings
    };
  }, [CANVAS_WIDTH, CANVAS_HEIGHT, PADDLE_HEIGHT, PADDLE_WIDTH, PADDLE_SPEED, difficulty]);

  // Reset ball
  const resetBall = useCallback((toPlayer = true) => {
    if (!gameRef.current) return;

    const game = gameRef.current;
    const speedSettings = game.speedSettings || getSpeedSettings(difficulty);

    game.ball.x = CANVAS_WIDTH / 2;
    game.ball.y = CANVAS_HEIGHT / 2;
    game.ball.speed = speedSettings.initial;

    // Random angle between -45 and 45 degrees
    const angle = (Math.random() - 0.5) * (Math.PI / 3);
    const direction = toPlayer ? -1 : 1;

    game.ball.vx = Math.cos(angle) * game.ball.speed * direction;
    game.ball.vy = Math.sin(angle) * game.ball.speed;
    game.lastResetTime = Date.now();
  }, [CANVAS_WIDTH, CANVAS_HEIGHT, difficulty]);

  // Start game
  const handleStart = useCallback(async () => {
    initGame();
    setPlayerScore(0);
    setAiScore(0);
    scoreRef.current = { player: 0, ai: 0 };
    setGameState('playing');

    setTimeout(() => {
      resetBall(Math.random() > 0.5);
    }, 100);

    if (visitorId) {
      const result = await API.startGame(visitorId, difficulty);
      if (result && result.success) {
        setGameSessionId(result.gameSessionId);
      }
    }
  }, [visitorId, difficulty, initGame, resetBall]);

  // Restart game
  const handleRestart = useCallback(async () => {
    initGame();
    setPlayerScore(0);
    setAiScore(0);
    scoreRef.current = { player: 0, ai: 0 };
    setGameState('playing');

    setTimeout(() => {
      resetBall(Math.random() > 0.5);
    }, 100);

    if (visitorId) {
      const result = await API.startGame(visitorId, difficulty);
      if (result && result.success) {
        setGameSessionId(result.gameSessionId);
      }
    }
  }, [visitorId, difficulty, initGame, resetBall]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      keysRef.current[e.key] = true;

      if ((e.key === 'p' || e.key === 'P' || e.key === 'Escape') && gameState === 'playing') {
        setGameState('paused');
      } else if ((e.key === 'p' || e.key === 'P' || e.key === 'Escape') && gameState === 'paused') {
        setGameState('playing');
      }
    };

    const handleKeyUp = (e) => {
      keysRef.current[e.key] = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameState]);

  // Mouse controls
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMouseMove = (e) => {
      if (!gameRef.current || gameState !== 'playing') return;
      const rect = canvas.getBoundingClientRect();
      const scaleY = CANVAS_HEIGHT / rect.height;
      const mouseY = (e.clientY - rect.top) * scaleY;

      gameRef.current.player.y = mouseY - PADDLE_HEIGHT / 2;

      // Keep paddle within bounds
      gameRef.current.player.y = Math.max(0, Math.min(gameRef.current.player.y, CANVAS_HEIGHT - PADDLE_HEIGHT));
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    return () => canvas.removeEventListener('mousemove', handleMouseMove);
  }, [gameState, CANVAS_HEIGHT, PADDLE_HEIGHT]);

  // Touch controls for mobile
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleTouch = (e) => {
      if (!gameRef.current || gameState !== 'playing') return;
      e.preventDefault();

      const touch = e.touches[0];
      const rect = canvas.getBoundingClientRect();
      const scaleY = CANVAS_HEIGHT / rect.height;
      const touchY = (touch.clientY - rect.top) * scaleY;

      gameRef.current.player.y = touchY - PADDLE_HEIGHT / 2;

      // Keep paddle within bounds
      gameRef.current.player.y = Math.max(0, Math.min(gameRef.current.player.y, CANVAS_HEIGHT - PADDLE_HEIGHT));
    };

    canvas.addEventListener('touchstart', handleTouch, { passive: false });
    canvas.addEventListener('touchmove', handleTouch, { passive: false });
    return () => {
      canvas.removeEventListener('touchstart', handleTouch);
      canvas.removeEventListener('touchmove', handleTouch);
    };
  }, [gameState, CANVAS_HEIGHT, PADDLE_HEIGHT]);

  // Main game loop
  useEffect(() => {
    if (gameState !== 'playing') return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let lastTime = performance.now();

    const update = (currentTime) => {
      const deltaTime = (currentTime - lastTime) / 16.67; // Normalize to 60fps
      lastTime = currentTime;

      if (!gameRef.current) return;
      const game = gameRef.current;

      // Clamp delta time to prevent huge jumps
      const dt = Math.min(deltaTime, 2);

      // Player keyboard movement
      if (keysRef.current['ArrowUp'] || keysRef.current['w'] || keysRef.current['W']) {
        game.player.y -= game.player.speed * dt;
      }
      if (keysRef.current['ArrowDown'] || keysRef.current['s'] || keysRef.current['S']) {
        game.player.y += game.player.speed * dt;
      }

      // Keep player paddle in bounds
      game.player.y = Math.max(0, Math.min(game.player.y, CANVAS_HEIGHT - PADDLE_HEIGHT));

      // AI movement with prediction
      const aiDifficultySpeed = difficulty === 'easy' ? 0.65 : difficulty === 'hard' ? 1.15 : 0.95;
      const aiReactionThreshold = difficulty === 'easy' ? 15 : difficulty === 'hard' ? 3 : 8;

      let aiTargetY = game.ball.y + BALL_SIZE / 2;

      // Predict where ball will be when it reaches AI paddle
      if (game.ball.vx > 0) {
        const timeToReach = (game.ai.x - game.ball.x) / Math.abs(game.ball.vx);
        const predictedY = game.ball.y + (game.ball.vy * timeToReach);

        // Clamp prediction to playable area
        aiTargetY = Math.max(PADDLE_HEIGHT / 2, Math.min(predictedY + BALL_SIZE / 2, CANVAS_HEIGHT - PADDLE_HEIGHT / 2));
      }

      const aiCenter = game.ai.y + PADDLE_HEIGHT / 2;
      const aiSpeed = game.ai.speed * aiDifficultySpeed * dt;

      // Move towards predicted position
      if (Math.abs(aiCenter - aiTargetY) > aiReactionThreshold) {
        if (aiCenter < aiTargetY) {
          game.ai.y += aiSpeed;
        } else {
          game.ai.y -= aiSpeed;
        }
      }

      // Keep AI paddle in bounds
      game.ai.y = Math.max(0, Math.min(game.ai.y, CANVAS_HEIGHT - PADDLE_HEIGHT));

      // Only move ball if enough time has passed since reset (prevent immediate scoring)
      if (Date.now() - game.lastResetTime > 500) {
        // Ball movement
        game.ball.x += game.ball.vx * dt;
        game.ball.y += game.ball.vy * dt;

        // Ball collision with top/bottom walls
        if (game.ball.y <= 0) {
          game.ball.y = 0;
          game.ball.vy = Math.abs(game.ball.vy);
        } else if (game.ball.y >= CANVAS_HEIGHT - BALL_SIZE) {
          game.ball.y = CANVAS_HEIGHT - BALL_SIZE;
          game.ball.vy = -Math.abs(game.ball.vy);
        }

        // Player paddle collision
        if (
          game.ball.vx < 0 &&
          game.ball.x <= game.player.x + PADDLE_WIDTH &&
          game.ball.x + BALL_SIZE >= game.player.x &&
          game.ball.y + BALL_SIZE >= game.player.y &&
          game.ball.y <= game.player.y + PADDLE_HEIGHT
        ) {
          // Bounce ball
          game.ball.x = game.player.x + PADDLE_WIDTH;
          game.ball.vx = Math.abs(game.ball.vx);

          // Add spin based on where ball hits paddle
          const hitPos = ((game.ball.y + BALL_SIZE / 2) - (game.player.y + PADDLE_HEIGHT / 2)) / (PADDLE_HEIGHT / 2);
          game.ball.vy = hitPos * game.ball.speed * 0.8;

          // Slightly increase speed (capped) - uses difficulty-based multiplier
          const speedSettings = game.speedSettings || getSpeedSettings(difficulty);
          game.ball.speed = Math.min(game.ball.speed * speedSettings.multiplier, speedSettings.max);

          // Update velocity to match new speed
          const currentSpeed = Math.sqrt(game.ball.vx * game.ball.vx + game.ball.vy * game.ball.vy);
          game.ball.vx = (game.ball.vx / currentSpeed) * game.ball.speed;
          game.ball.vy = (game.ball.vy / currentSpeed) * game.ball.speed;
        }

        // AI paddle collision
        if (
          game.ball.vx > 0 &&
          game.ball.x + BALL_SIZE >= game.ai.x &&
          game.ball.x <= game.ai.x + PADDLE_WIDTH &&
          game.ball.y + BALL_SIZE >= game.ai.y &&
          game.ball.y <= game.ai.y + PADDLE_HEIGHT
        ) {
          // Bounce ball
          game.ball.x = game.ai.x - BALL_SIZE;
          game.ball.vx = -Math.abs(game.ball.vx);

          // Add spin
          const hitPos = ((game.ball.y + BALL_SIZE / 2) - (game.ai.y + PADDLE_HEIGHT / 2)) / (PADDLE_HEIGHT / 2);
          game.ball.vy = hitPos * game.ball.speed * 0.8;

          // Slightly increase speed (capped) - uses difficulty-based multiplier
          const speedSettingsAI = game.speedSettings || getSpeedSettings(difficulty);
          game.ball.speed = Math.min(game.ball.speed * speedSettingsAI.multiplier, speedSettingsAI.max);

          // Update velocity
          const currentSpeed = Math.sqrt(game.ball.vx * game.ball.vx + game.ball.vy * game.ball.vy);
          game.ball.vx = (game.ball.vx / currentSpeed) * game.ball.speed;
          game.ball.vy = (game.ball.vy / currentSpeed) * game.ball.speed;
        }

        // Scoring - Player misses (ball goes past left edge)
        if (game.ball.x < -BALL_SIZE) {
          scoreRef.current.ai++;
          setAiScore(scoreRef.current.ai);

          if (scoreRef.current.ai >= WINNING_SCORE) {
            setGameState('gameOver');
            if (gameSessionId) {
              API.endGame(gameSessionId, scoreRef.current.player, scoreRef.current.ai);
            }
          } else {
            resetBall(true);
          }
        }

        // Scoring - AI misses (ball goes past right edge)
        if (game.ball.x > CANVAS_WIDTH) {
          scoreRef.current.player++;
          setPlayerScore(scoreRef.current.player);

          if (scoreRef.current.player >= WINNING_SCORE) {
            setGameState('gameOver');
            if (gameSessionId) {
              API.endGame(gameSessionId, scoreRef.current.player, scoreRef.current.ai);
            }
          } else {
            resetBall(false);
          }
        }
      }
    };

    const render = () => {
      if (!gameRef.current) return;
      const game = gameRef.current;

      // Clear canvas
      ctx.fillStyle = '#0a0e27';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Draw center line
      ctx.strokeStyle = 'rgba(99, 102, 241, 0.2)';
      ctx.lineWidth = 2;
      ctx.setLineDash([10, 10]);
      ctx.beginPath();
      ctx.moveTo(CANVAS_WIDTH / 2, 0);
      ctx.lineTo(CANVAS_WIDTH / 2, CANVAS_HEIGHT);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw paddles
      ctx.fillStyle = '#6366f1';
      ctx.shadowBlur = 15;
      ctx.shadowColor = '#6366f1';
      ctx.fillRect(game.player.x, game.player.y, PADDLE_WIDTH, PADDLE_HEIGHT);
      ctx.fillRect(game.ai.x, game.ai.y, PADDLE_WIDTH, PADDLE_HEIGHT);

      // Draw ball
      ctx.fillStyle = '#818cf8';
      ctx.shadowBlur = 20;
      ctx.shadowColor = '#818cf8';
      ctx.beginPath();
      ctx.arc(game.ball.x + BALL_SIZE / 2, game.ball.y + BALL_SIZE / 2, BALL_SIZE / 2, 0, Math.PI * 2);
      ctx.fill();

      ctx.shadowBlur = 0;

      // Draw scores
      ctx.fillStyle = 'rgba(232, 234, 246, 0.3)';
      ctx.font = '600 56px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(scoreRef.current.player, CANVAS_WIDTH / 2 - 100, 70);
      ctx.fillText(scoreRef.current.ai, CANVAS_WIDTH / 2 + 100, 70);
    };

    const gameLoop = (currentTime) => {
      update(currentTime);
      render();
      animationFrameRef.current = requestAnimationFrame(gameLoop);
    };

    animationFrameRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [gameState, difficulty, gameSessionId, resetBall, CANVAS_WIDTH, CANVAS_HEIGHT, PADDLE_WIDTH, PADDLE_HEIGHT, BALL_SIZE, WINNING_SCORE]);

  return (
    <div className="pong-game">
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className="game-canvas"
      />

      {gameState === 'start' && (
        <div className="game-overlay">
          <h2>Pong</h2>
          <div className="difficulty-selector">
            <label>Difficulty</label>
            <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
          <button onClick={handleStart} className="game-button">Start Game</button>
          <p className="controls-hint">Arrow Keys, WASD, or Mouse</p>
        </div>
      )}

      {gameState === 'paused' && (
        <div className="game-overlay">
          <h2>Paused</h2>
          <p>Press P or ESC to resume</p>
        </div>
      )}

      {gameState === 'gameOver' && (
        <div className="game-overlay">
          <h2>Game Over</h2>
          <h3>{playerScore > aiScore ? 'You Win' : 'AI Wins'}</h3>
          <p className="final-score">{playerScore} — {aiScore}</p>
          <button onClick={handleRestart} className="game-button">Play Again</button>
        </div>
      )}
    </div>
  );
};

export default PongGame;
